import { CharacterService } from "@moe/priv/model/character";
import { DeepPartial } from "@moe/priv/types/types";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { SearchDescription } from "@web/components/search/SearchDescription";
import { Pagination } from "@web/route-services/index/Pagination";
import { TagDisplay } from "@web/route-services/index/TagDisplay";
import { useComputedNSFW } from "@web/route-services/index/use-computed-nsfw";
import { useFocusExploreTitle } from "@web/route-services/index/use-focus-explore-title";
import { PAGE_SIZE } from "@web/route-services/index/utils";
import { VerticalCard } from "@web/route-services/index/VerticalCard";
import { useAppContext } from "@web/route-services/root/AppContext";
import { Route } from "@web/routes";
import { motion } from "motion/react";
import { useMemo, useRef } from "react";
import { ExploreControls } from "./ExploreControls";

export const exploreCharactersQK = "exploreCards";

export function Explore() {
  const { access, settings } = useAppContext();
  const {
    ranking = "message_count",
    searchText = "",
    period = "all",
    order = "desc",
    page = 1,
    pageSize = PAGE_SIZE,
    tags
  } = Route.useSearch();

  const titleRef = useRef<HTMLDivElement>(null);
  const { isDesktopScreen, doFocusExploreRef } = useAppContext();
  const nsfwOK = useComputedNSFW();
  const blacklistedTags = settings?.blacklistedTags;
  const nav = useNavigate({ from: Route.fullPath });

  const isSearching = searchText || tags?.length;

  const pageOptions: DeepPartial<CharacterService.GetPageOptions> = {
    // Page is displayed as 1-indexed, but the backend expects 0-indexed
    page: page - 1,
    pageSize,
    order,
    ranking,
    filter: {
      period,
      searchText,
      nsfwOK,
      tags,
      blacklistedTags
    }
  };

  const {
    data: charactersData,
    isError,
    isPending,
    isFetching
  } = useQuery({
    queryKey: [exploreCharactersQK, pageOptions, tags],
    queryFn: async () => {
      return await access.character.getPage(pageOptions);
    }
  });

  // TODO: this should belong in the same react query as the characters query
  // So that it could be consistently invalidated when the characters query is invalidated
  const { data: lastPage } = useQuery({
    queryKey: ["lastPage", pageOptions.pageSize, pageOptions.filter],
    queryFn: async () => {
      const options = {
        pageSize: pageOptions.pageSize,
        filter: pageOptions.filter
      };
      return await access.character.getLastPageNumber(options);
    }
  });

  useFocusExploreTitle({ titleRef: titleRef as React.RefObject<HTMLDivElement>, isFetching });

  const characters = useMemo(() => charactersData?.flatMap((card) => card) || [], [charactersData]);

  const pageChangeHandler = (newPage: number, focusTitle: boolean) => {
    doFocusExploreRef.current = focusTitle;
    nav({ search: (prev) => ({ ...prev, page: newPage }) });
  };

  return (
    <div className="flex flex-col">
      <div className="mx-2 flex flex-col md:mx-0">
        <div className="mb-3 flex flex-col justify-center">
          <h2 className="text-tx-primary pt-8 text-2xl font-semibold md:text-3xl" ref={titleRef}>
            {isSearching ? "🔍 Search" : "🧭 Explore"}
          </h2>

          <SearchDescription
            className="mb-2"
            searchText={searchText}
            tags={tags}
            onClear={() => {
              nav({ to: "/" });
            }}
          />
        </div>
        {isDesktopScreen && <ExploreControls />}
        {!isDesktopScreen && <TagDisplay />}
      </div>
      <Pagination
        currentPage={page}
        totalPages={lastPage}
        onPageChange={(page) => pageChangeHandler(page, false)}
        className="mb-2 lg:-mt-5"
      />

      {isError && <div className="text-err text-center">Error loading cards. Please try again.</div>}
      {characters && characters.length === 0 && !isPending && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="flex h-full w-full items-center justify-center pb-10"
        >
          <p className="text-tx-tertiary select-none text-center text-base font-medium leading-9">
            No characters found. <br />
            (つ╥﹏╥)つ
          </p>
        </motion.div>
      )}
      {isPending && (
        <div
          className={
            "mb-14 grid grid-cols-2 justify-center gap-1.5 scroll-smooth px-1.5 transition duration-500 ease-out sm:grid-cols-3 md:flex md:flex-wrap md:gap-4 md:px-0"
          }
        >
          {Array.from({ length: PAGE_SIZE }, (_, i) => i).map((i) => (
            <VerticalCard key={i} isPending={isPending} />
          ))}
        </div>
      )}
      {!isPending && !isError && (
        <div
          className={
            "mb-14 grid grid-cols-2 justify-center gap-1.5 scroll-smooth px-1.5 transition duration-500 ease-out sm:grid-cols-3 md:flex md:flex-wrap md:gap-4 md:px-0"
          }
        >
          {characters.map((char) => (
            <VerticalCard key={char.id} char={char} isPending={isPending} />
          ))}
        </div>
      )}
      <Pagination
        currentPage={page}
        totalPages={lastPage}
        onPageChange={(page) => pageChangeHandler(page, true)}
        className="-mt-4"
      />
    </div>
  );
}
